import apiService from "@/lib/api";

const BULK_INTAKE_BASE = "/api/v2/bulk-intake";
const AUTH_BASE = "/api/v2/auth";

export const BulkIntakeService = {
  /**
   * Fetches the exact legal consent/attestation wording to render next to the
   * consent checkbox (and optionally on the "request confirmation" screen).
   *
   * @returns {Promise<{attestation_text: string, email_verification_text: string}>}
   */
  getConsentText: async () => {
    try {
      const response = await apiService.getSecure(`${BULK_INTAKE_BASE}/consent-text`);
      return response.data;
    } catch (error) {
      console.error("Error fetching bulk intake consent text:", error);
      throw error;
    }
  },

  /**
   * Step 1 — Creates a new bulk intake batch against an open job.
   *
   * @param {string} jobId - The job to bulk-upload resumes onto
   * @returns {Promise<{batch_id: string, job_id: string, status: string}>}
   */
  createBatch: async (jobId) => {
    try {
      const response = await apiService.postSecure(`${BULK_INTAKE_BASE}`, { job_id: jobId });
      return response.data;
    } catch (error) {
      console.error("Error creating bulk intake batch:", error);
      throw error;
    }
  },

  /**
   * Step 2 — Uploads a single resume file into a batch. Call once per file
   * (not once for all files) so per-file progress/success/failure can be
   * tracked independently in the UI.
   *
   * @param {string} batchId - The batch to upload into
   * @param {File} resumeFile - The resume file (.pdf/.doc/.docx, <=20MB)
   * @param {Object} config - Optional Axios config (signal, onUploadProgress, etc.)
   * @returns {Promise<{item_id: string, file_name: string, status: string}>}
   */
  uploadResumeItem: async (batchId, resumeFile, config = {}) => {
    try {
      const formData = new FormData();
      formData.append("resume", resumeFile);

      const response = await apiService.postSecure(`${BULK_INTAKE_BASE}/${batchId}/items`, formData, {
        ...config,
        headers: {
          ...(config.headers || {}),
          "Content-Type": "multipart/form-data",
        },
      });
      return response.data;
    } catch (error) {
      console.error(`Error uploading resume item to batch ${batchId}:`, error);
      throw error;
    }
  },

  /**
   * Step 3 — Recruiter ticks the single consent checkbox; this kicks off
   * background AI extraction for every resume uploaded so far. Returns
   * immediately without waiting for extraction to finish.
   *
   * @param {string} batchId
   * @returns {Promise<{batch_id: string, status: string, item_count: number}>}
   */
  giveBatchConsent: async (batchId) => {
    try {
      const response = await apiService.postSecure(`${BULK_INTAKE_BASE}/${batchId}/consent`, {
        consent: true,
      });
      return response.data;
    } catch (error) {
      console.error(`Error giving consent for batch ${batchId}:`, error);
      throw error;
    }
  },

  /**
   * Step 6 — Fetches the full batch + item list. Use on initial page load,
   * or to resync state after a dropped WebSocket connection.
   *
   * @param {string} batchId
   * @returns {Promise<Object>} Batch with `items[]`, each carrying status/draft_data
   */
  getBatch: async (batchId) => {
    try {
      const response = await apiService.getSecure(`${BULK_INTAKE_BASE}/${batchId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching bulk intake batch ${batchId}:`, error);
      throw error;
    }
  },

  /**
   * Step 7 — Streams the original resume file (PDF/DOC/DOCX) for an item, to
   * render side-by-side with the editable draft_data form.
   *
   * @param {string} itemId
   * @returns {Promise<any>} Axios response with blob data (raw file bytes)
   */
  fetchItemResumeFile: async (itemId) => {
    try {
      const response = await apiService.getSecure(`${BULK_INTAKE_BASE}/item/${itemId}/resume-file`, {
        responseType: "blob",
      });
      return response;
    } catch (error) {
      console.error(`Error fetching resume file for item ${itemId}:`, error);
      throw error;
    }
  },

  /**
   * Step 8 — Saves recruiter edits to an item's extracted draft data.
   *
   * `alternateEmail` is a top-level sibling of `draft_data`, never nested
   * inside it — the server ignores it silently if it's misplaced there.
   * Pass `null` to clear a previously-stored value, `undefined` to leave it.
   *
   * @param {string} itemId
   * @param {Object} draftData - Same shape as candidate self-apply review data
   * @param {string|null} [alternateEmail]
   * @returns {Promise<{item_id: string, status: string, recruiter_reviewed: boolean}>}
   */
  saveItemEdits: async (itemId, draftData, alternateEmail) => {
    try {
      const response = await apiService.putSecure(`${BULK_INTAKE_BASE}/items/${itemId}`, {
        draft_data: draftData,
        ...(alternateEmail !== undefined && { alternate_email: alternateEmail }),
      });
      return response.data;
    } catch (error) {
      console.error(`Error saving edits for bulk intake item ${itemId}:`, error);
      throw error;
    }
  },

  /**
   * Step 8 (alt) — Removes a single resume from the batch.
   *
   * @param {string} itemId
   * @returns {Promise<{item_id: string, status: string, recruiter_reviewed: boolean}>}
   */
  discardItem: async (itemId) => {
    try {
      const response = await apiService.putSecure(`${BULK_INTAKE_BASE}/items/${itemId}`, {
        discard: true,
      });
      return response.data;
    } catch (error) {
      console.error(`Error discarding bulk intake item ${itemId}:`, error);
      throw error;
    }
  },

  /**
   * Recruiter chose "Continue Anyway" on a validation-warning item — resumes
   * extraction in the background from the cached resume text. Does not
   * re-run validation.
   *
   * @param {string} itemId
   * @returns {Promise<{item_id: string, status: string}>}
   */
  continueItemExtraction: async (itemId) => {
    try {
      const response = await apiService.postSecure(`${BULK_INTAKE_BASE}/items/${itemId}/continue`);
      return response.data;
    } catch (error) {
      console.error(`Error continuing bulk intake item ${itemId} after validation warning:`, error);
      throw error;
    }
  },

  /**
   * Retries extraction for a single FAILED (or MANUAL_PENDING) item. Answers
   * immediately with the updated retry counters; the result streams via
   * `bulk_intake_item_ready`/`bulk_intake_item_failed` as usual.
   *
   * @param {string} itemId
   * @returns {Promise<{item_id: string, status: string, retry_count: number, retries_remaining: number}>}
   */
  retryItemExtraction: async (itemId) => {
    try {
      const response = await apiService.postSecure(`${BULK_INTAKE_BASE}/items/${itemId}/retry`);
      return response.data;
    } catch (error) {
      console.error(`Error retrying extraction for bulk intake item ${itemId}:`, error);
      throw error;
    }
  },

  /**
   * Switches a FAILED item into manual entry — the recruiter types the
   * candidate's details from scratch instead of retrying extraction.
   *
   * @param {string} itemId
   * @returns {Promise<{item_id: string, status: string, entry_mode: string, required_fields: string[]}>}
   */
  startManualEntry: async (itemId) => {
    try {
      const response = await apiService.postSecure(`${BULK_INTAKE_BASE}/items/${itemId}/manual`);
      return response.data;
    } catch (error) {
      console.error(`Error starting manual entry for bulk intake item ${itemId}:`, error);
      throw error;
    }
  },

  /**
   * Step 9a — Fetches the recruiter's account email to prefill the
   * "Confirm All" screen. Always re-fetch right before requesting
   * confirmation rather than caching from earlier in the session.
   *
   * @returns {Promise<{email: string|null}>}
   */
  getAccountEmail: async () => {
    try {
      const response = await apiService.getSecure(`${AUTH_BASE}/email`);
      return response.data;
    } catch (error) {
      console.error("Error fetching account email:", error);
      throw error;
    }
  },

  /**
   * Step 9b — Corrects the recruiter's account email, if needed, before
   * requesting the confirmation link. Fully audited server-side.
   *
   * The address an account recovers through does not move on a bearer token
   * alone: `/auth/email` verifies the current password exactly as
   * `/auth/password/change` does, so the recruiter re-types it here.
   *
   * @param {string} email
   * @param {string} currentPassword
   * @returns {Promise<{success: boolean, email: string}>}
   */
  updateAccountEmail: async (email, currentPassword) => {
    try {
      const response = await apiService.patch(`${AUTH_BASE}/email`, {
        email,
        currentPassword,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating account email:", error);
      throw error;
    }
  },

  /**
   * Step 9c — Requests batch confirmation. Branch on `email_confirmation_required`
   * in the response rather than on a pre-read tenant setting:
   *
   * * `true`  — a magic-link email was sent; `email_masked` says where, and the
   *   batch is only submitted once the recruiter clicks it.
   * * `false` — this job has its bulk email step off, so THIS call was
   *   the finalize: the batch is already submitted and the response carries the
   *   same counts `/confirm/finalize` would have returned. It does the whole
   *   per-resume finalize inline, so it can be slow on a large batch — never
   *   put a short client timeout on it.
   *
   * `email` is optional and ignored in single-factor mode, but keep passing it:
   * in two-factor mode the server still verifies it against the account email,
   * and the caller can't know which mode it's in beforehand. Must be exactly
   * the email just displayed/confirmed via getAccountEmail/updateAccountEmail.
   * Safe to call again to resend/invalidate a previous link.
   *
   * @param {string} batchId
   * @param {string} [email]
   * @returns {Promise<{message: string, email_masked: string|null, email_confirmation_required: boolean, batch_id: string|null, confirmed_count: number|null, dropped_count: number|null, dropped_files: Array<{file_name: string, reason: "duplicate"|"error"}>}>}
   */
  requestBatchConfirmation: async (batchId, email) => {
    try {
      const response = await apiService.postSecure(`${BULK_INTAKE_BASE}/${batchId}/request-confirmation`, {
        email,
      });
      return response.data;
    } catch (error) {
      console.error(`Error requesting confirmation email for batch ${batchId}:`, error);
      throw error;
    }
  },

  /**
   * Step 10 — Finalizes the batch using the token from the emailed magic
   * link. No auth header is required; the token itself is the credential.
   * Call as a POST from the app route the email link points to.
   *
   * @param {string} token
   * @returns {Promise<{batch_id: string, confirmed_count: number, dropped_count: number, dropped_files: Array<{file_name: string, reason: string}>, message: string}>}
   */
  finalizeBatchConfirmation: async (token) => {
    try {
      const response = await apiService.post(`${BULK_INTAKE_BASE}/confirm/finalize?token=${token}`);
      return response.data;
    } catch (error) {
      console.error("Error finalizing bulk intake batch confirmation:", error);
      throw error;
    }
  },

  /**
   * Mints a fresh batch-confirmation link for a token in any state — live,
   * expired, or replaced by an earlier resend. No auth header; the token is
   * the credential. Only ever emails the recruiter's own inbox.
   *
   * @param {string} token
   * @returns {Promise<{message: string, email_masked: string|null, expires_in_minutes: number, sends_remaining: number}>}
   */
  resendBatchConfirmation: async (token) => {
    try {
      const response = await apiService.post(`${BULK_INTAKE_BASE}/confirm/resend?token=${token}`);
      return response.data;
    } catch (error) {
      console.error("Error resending bulk intake batch confirmation:", error);
      throw error;
    }
  },
};